import { useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setGoHome, setProceedHotelSelection } from '../Redux/Reducers/appConfigReducer';
import { setLocationSelectErrorPopup } from '../Redux/Reducers/hotelSelectorReducer';
import { setRenderInfoPopup, setRenderLoader } from '../Redux/Actions/appConfigAction';
import { handleLocationSelect } from '../js/hotelSelector';

/**
 * Custom hook to manage hotel selection logic
 * Handles hotel switching, confirmation popups, and navigation
 */
export const useHotelSelection = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { proceedHotelSelection } = useSelector(
        state => ({ proceedHotelSelection: state.appConfigSlice.proceedHotelSelection }),
        shallowEqual
    );

    const { hotelListData, locationSelectionErrorPopup } = useSelector(
        state => state.hotelSelectorSlice,
        shallowEqual
    );

    const handleContinueHotelSelection = () => {
        dispatch(setProceedHotelSelection({ init: true, hotel_Code: locationSelectionErrorPopup.selectedHotel.hotelPropertyKey, forceContinue: true }));
        handleCloseHotelSelectorPopup();
    };

    const handleCloseHotelSelectorPopup = () => {
        dispatch(setRenderInfoPopup(false));
        dispatch(setLocationSelectErrorPopup({ isVisible: false }));
    };

    // Show confirmation popup when hotel selection has an error/warning
    useEffect(() => {
        if (locationSelectionErrorPopup.isVisible) {
            dispatch(setRenderInfoPopup(true, {
                title: t('hotelListWidget.confirmation'),
                subtitle: t('hotelListWidget.confirmation_subtitle'),
                onClose: handleCloseHotelSelectorPopup,
                secondaryBtnLabel: "No",
                onContinue: handleContinueHotelSelection,
                primaryBtnLabel: "Yes"
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationSelectionErrorPopup.isVisible]);

    // Handle programmatic hotel selection (e.g., from deep link)
    useEffect(() => {
        if (hotelListData && proceedHotelSelection.init) {
            const required_hotel_code = proceedHotelSelection.hotel_Code;
            const hotelListsInfo = hotelListData?.payload?.data;

            const forceContinue = proceedHotelSelection.forceContinue || false;

            dispatch(setProceedHotelSelection(false));

            for (const hotelDataWithRegion of hotelListsInfo) {
                if (hotelDataWithRegion.hotelList && hotelDataWithRegion.hotelList?.length > 0) {
                    for (const hotelData of hotelDataWithRegion.hotelList) {
                        if (hotelData.hotelPropertyKey === required_hotel_code) {
                            dispatch(setRenderLoader(true, "Redirecting..."));
                            dispatch(handleLocationSelect(hotelData, (result) => {
                                dispatch(setRenderLoader(false));
                                if (result.status === "ALREADY_AT_SELECTED_HOTEL") {
                                    console.log("Same hotel selected, no changes made.");
                                    dispatch(setGoHome(true));
                                }
                            }, forceContinue));
                            return; // Exit after finding and selecting hotel
                        }
                    }
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hotelListData, proceedHotelSelection]);

    return null;
};
